import React, { useCallback } from 'react';
import { List, StatusTip } from 'tea-component';
import { useHistory } from 'umi';
import CardCommon from '../../CardCommon';
import PostContentItem from '../PostContentItem';
import './index.scss';
import { LanguageType } from '@op/types/common';
import { DBO_U } from '@op/utils/format';
import { goToFeedsReviewsDetail } from '@op/utils/url';
import { JumpType } from '@home/utils/utils';
import { DBO_POINT } from '@op/operationLog/buriedPoint';

interface PostContentListProps {
    /** 全部内容数据 */
    items: any[];
    language: LanguageType;
    /** 是否正在首次加载（无数据时显示整体 loading） */
    loading?: boolean;
    /** 是否正在加载更多（有数据时在列表底部显示加载指示） */
    loadingMore?: boolean;
    /** 是否禁用内部滚动加载（true 时由父级组件管理滚动） */
    disableScrollLoad?: boolean;
    /** 滚动到底部时触发（disableScrollLoad=false 时生效） */
    onScrollBottom?: () => void;
    loadingHeight?: number;
    height?: number;
    onClickDetail?: (data: any) => void;
    onClickLink?: (data: any, url: string) => void;
    oriPointKey?: string;
    deatilPointKey?: string;
}

/**
 * 帖子/评论内容列表 —— 使用 List 组件渲染，支持滚动触底回调
 * - disableScrollLoad=false（默认）：内部 List 组件管理滚动加载
 * - disableScrollLoad=true：滚动由父级组件管理，仅负责内容渲染
 */
export default function PostContentList({
    items,
    language,
    loading = false,
    loadingMore = false,
    disableScrollLoad = false,
    onScrollBottom,
    loadingHeight = 200,
    height = 500,
    onClickDetail,
    onClickLink,
    oriPointKey,
    deatilPointKey
}: PostContentListProps) {
    const history = useHistory();

    const handleScrollBottom = useCallback(() => {
        if (!disableScrollLoad) {
            onScrollBottom?.();
        }
    }, [disableScrollLoad, onScrollBottom]);

    /** 默认：查看详情 - 跳转 Feeds Review 详情页 */
    const getClickDetail = useCallback(
        (data: any) => {
            const handleDetail = onClickDetail
                ? () => onClickDetail(data)
                : () => {
                    const { location: hookLocation } = history;
                    const { pathname } = hookLocation || {};
                    const { search } = window.location;
                    const query = DBO_U.queryObj(search);

                    const commonParams = {
                        language,
                        redirect_path: encodeURIComponent(DBO_U.stripBasePath(pathname + search)),
                        gameid: query.gameid,
                    };

                    goToFeedsReviewsDetail(history, data, commonParams, JumpType.Window);
                };

            return () => {
                if (deatilPointKey) DBO_POINT.btn(deatilPointKey, data.comment_uin || data.comment_id);
                handleDetail();
            };
        },
        [history, language, onClickDetail, deatilPointKey],
    );

    const getClickLink = useCallback(
        (data: any, url: string) => {
            const handleLink = onClickLink
                ? () => onClickLink(data, url)
                : () => DBO_U.open(url);

            return () => {
                if (oriPointKey) DBO_POINT.btn(oriPointKey, data.comment_uin || data.comment_id);
                handleLink();
            };
        },
        [onClickLink, oriPointKey],
    );

    /** 默认：获取原帖链接 */
    const getLinkUrl = useCallback((data: any) => {
        const link = data?.content_url;
        return DBO_U.isUrl(link) ? link.trim() : '';
    }, []);

    return (
        <CardCommon contentCard className="dbo-PostContentList-card" bodyStyle={{ height }} empty={!items.length} emptyHeight={height} loading={loading && items.length === 0} loadingHeight={loadingHeight}>
            <List onScrollBottom={disableScrollLoad ? undefined : handleScrollBottom} className="dbo-PostContentList-list">
                {items.map((contentItem: any) => {
                    const url = getLinkUrl(contentItem);
                    return (
                        <List.Item
                            key={contentItem.comment_uin || contentItem.comment_id}
                            className="dbo-PostContentList-list-item"
                        >
                            <PostContentItem
                                item={contentItem}
                                language={language}
                                onClickDetail={getClickDetail(contentItem)}
                                linkUrl={url}
                                onClickLink={getClickLink(contentItem, url)}
                            />
                        </List.Item>
                    )
                })}
                {loadingMore && (
                    <List.Item className="dbo-PostContentList-loading-more">
                        <StatusTip status="loading" />
                    </List.Item>
                )}
            </List>
        </CardCommon>

    );
}
